/**
 * Profile page component
 */
import {
    Add,
    Business,
    CalendarToday,
    ContentCopy,
    DeleteOutline,
    Email,
    ExpandMore,
    Person,
    Verified,
    VpnKey,
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { usersApi } from '@/api/users';
import { useAuth } from '@/hooks/useAuth';
import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';
import { ApiKeyInfo, UserMe, UserRole } from '@/types';

export const ProfilePage: React.FC = () => {
  useRegisterPageHelp('Profile');
  const { user: authUser, updateUser } = useAuth();
  const [user, setUser] = useState<UserMe | null>(authUser);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [apiKeyName, setApiKeyName] = useState('');
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [deletingApiKey, setDeletingApiKey] = useState(false);
  const [createdApiKeyValue, setCreatedApiKeyValue] = useState<string | null>(null);
  const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKeyInfo | null>(null);
  const [requestExampleTab, setRequestExampleTab] = useState<'curl' | 'python' | 'javascript' | 'csharp'>('curl');
  const [recoveryExampleTab, setRecoveryExampleTab] = useState<'curl' | 'python' | 'javascript' | 'csharp'>('curl');

  // Form state
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
  });

  // Load user profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const profile = await usersApi.getProfile();
      setUser(profile);
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
      });

      try {
        setApiKeysLoading(true);
        const userApiKeys = await usersApi.getApiKeys();
        setApiKeys(userApiKeys);
      } catch {
        setApiKeys([]);
      } finally {
        setApiKeysLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      const updatedUser = await usersApi.updateProfile(formData);
      setUser(updatedUser);
      updateUser(updatedUser); // Update auth context
      setIsEditing(false);
      setSuccess('Profile updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
    });
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const canCreateMoreApiKeys = apiKeys.length < 5;

  const handleCreateApiKey = async () => {
    try {
      setCreatingApiKey(true);
      setError(null);
      setSuccess(null);

      const trimmedName = apiKeyName.trim();
      if (!trimmedName) {
        setError('API key name cannot be empty');
        return;
      }

      const response = await usersApi.createApiKey({ name: trimmedName });
      setCreatedApiKeyValue(response.key);
      setApiKeyName('');
      setSuccess('API key added successfully');

      setApiKeysLoading(true);
      const updatedKeys = await usersApi.getApiKeys();
      setApiKeys(updatedKeys);
    } catch (err: any) {
      setError(err.detail || err.message || 'Failed to create API key');
    } finally {
      setApiKeysLoading(false);
      setCreatingApiKey(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!apiKeyToDelete) {
      return;
    }

    try {
      setDeletingApiKey(true);
      setError(null);
      setSuccess(null);

      await usersApi.deleteApiKey(apiKeyToDelete.id);
      setApiKeys(prev => prev.filter(key => key.id !== apiKeyToDelete.id));
      setSuccess('API key deleted');
      setApiKeyToDelete(null);
    } catch (err: any) {
      setError(err.detail || err.message || 'Failed to delete API key');
    } finally {
      setDeletingApiKey(false);
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'Administrator';
      case UserRole.ORGANIZATION_ADMIN:
        return 'Organization Admin';
      case UserRole.USER:
        return 'User';
      default:
        return role;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'error';
      case UserRole.ORGANIZATION_ADMIN:
        return 'warning';
      case UserRole.USER:
        return 'primary';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Alert severity="error">
          Failed to load profile data
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Typography variant="h6" component="h2">
                <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                Personal Information
              </Typography>

              {!isEditing && (
                <Button variant="outlined" onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  disabled={!isEditing}
                  variant={isEditing ? 'outlined' : 'filled'}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={user.email}
                  disabled
                  variant="filled"
                  InputProps={{
                    startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" component="h2">
                    <VpnKey sx={{ mr: 1, verticalAlign: 'middle' }} />
                    API keys
                  </Typography>
                  <Chip
                    size="small"
                    label={`${apiKeys.length}/5`}
                    color={canCreateMoreApiKeys ? 'default' : 'warning'}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create up to 5 API keys.
                </Typography>

                <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                  <TextField
                    label="API key name"
                    value={apiKeyName}
                    onChange={(e) => setApiKeyName(e.target.value)}
                    size="small"
                    sx={{ minWidth: 280 }}
                    disabled={!canCreateMoreApiKeys || creatingApiKey}
                  />
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleCreateApiKey}
                    disabled={!canCreateMoreApiKeys || creatingApiKey}
                  >
                    {creatingApiKey ? 'Creating...' : 'Add key'}
                  </Button>
                </Box>

                {!canCreateMoreApiKeys && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Maximum number of active API keys reached (5/5).
                  </Alert>
                )}

                {apiKeysLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading API keys...
                    </Typography>
                  </Box>
                ) : apiKeys.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No API keys yet.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {apiKeys.map((apiKey) => (
                      <ListItem key={apiKey.id} divider disableGutters>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                          gap={2}
                        >
                          <ListItemText
                            primary={apiKey.name}
                            secondary={`Created: ${formatDate(apiKey.created_at)}`}
                          />
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<DeleteOutline />}
                            onClick={() => setApiKeyToDelete(apiKey)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" component="h3" gutterBottom>
                  Public API examples
                </Typography>
                <Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      GET /public-api/v1/health
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Tabs
                      value={requestExampleTab}
                      onChange={(_, value) => setRequestExampleTab(value)}
                      variant="scrollable"
                      allowScrollButtonsMobile
                      sx={{ mb: 1 }}
                    >
                      <Tab label="curl" value="curl" />
                      <Tab label="Python" value="python" />
                      <Tab label="JavaScript" value="javascript" />
                      <Tab label="C#" value="csharp" />
                    </Tabs>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Request example
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 2,
                      }}
                    >
{requestExampleTab === 'curl' ? `curl -X GET "https://app.quickfield.ai/public-api/v1/health" \\
  -H "apikey: YOUR_API_KEY"` : ''}
{requestExampleTab === 'python' ? `import requests

url = "https://app.quickfield.ai/public-api/v1/health"
headers = {"apikey": "YOUR_API_KEY"}

response = requests.get(url, headers=headers, timeout=10)
print(response.status_code)
print(response.json())` : ''}
{requestExampleTab === 'javascript' ? `const response = await fetch("https://app.quickfield.ai/public-api/v1/health", {
  method: "GET",
  headers: {
    apikey: "YOUR_API_KEY",
  },
});

console.log(response.status);
console.log(await response.json());` : ''}
{requestExampleTab === 'csharp' ? `using var client = new HttpClient();
client.DefaultRequestHeaders.Add("apikey", "YOUR_API_KEY");

var response = await client.GetAsync("https://app.quickfield.ai/public-api/v1/health");
var body = await response.Content.ReadAsStringAsync();

Console.WriteLine((int)response.StatusCode);
Console.WriteLine(body);` : ''}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      200 response example
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 1,
                      }}
                    >
{`{
  "status": "ok",
  "message": "API key is valid",
  "code": 200
}`}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      POST /public-api/v1/recovery_factor
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Tabs
                      value={recoveryExampleTab}
                      onChange={(_, value) => setRecoveryExampleTab(value)}
                      variant="scrollable"
                      allowScrollButtonsMobile
                      sx={{ mb: 1 }}
                    >
                      <Tab label="curl" value="curl" />
                      <Tab label="Python" value="python" />
                      <Tab label="JavaScript" value="javascript" />
                      <Tab label="C#" value="csharp" />
                    </Tabs>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Request example (without Monte Carlo)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      You can pass any number of supported attributes. If an attribute is unknown, do not include it in the request.
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 2,
                      }}
                    >
{recoveryExampleTab === 'curl' ? `curl -X POST "https://app.quickfield.ai/public-api/v1/recovery_factor" \\
  -H "Content-Type: application/json" \\
  -H "apikey: YOUR_API_KEY" \\
  -d '{
    "attributes": {
      "...": "see full payload below"
    }
  }'` : ''}
{recoveryExampleTab === 'python' ? `import requests

url = "https://app.quickfield.ai/public-api/v1/recovery_factor"
headers = {
    "apikey": "YOUR_API_KEY",
    "Content-Type": "application/json",
}
payload = {
    "attributes": {
        # See full payload with all supported attributes below
    }
}

response = requests.post(url, headers=headers, json=payload, timeout=20)
print(response.status_code)
print(response.json())` : ''}
{recoveryExampleTab === 'javascript' ? `const response = await fetch("https://app.quickfield.ai/public-api/v1/recovery_factor", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    apikey: "YOUR_API_KEY",
  },
  body: JSON.stringify({
    attributes: {
      // See full payload with all supported attributes below
    },
  }),
});

console.log(response.status);
console.log(await response.json());` : ''}
{recoveryExampleTab === 'csharp' ? `using System.Net.Http.Headers;
using System.Text;
using System.Text.Json;

using var client = new HttpClient();
client.DefaultRequestHeaders.Add("apikey", "YOUR_API_KEY");

var payload = new
{
    attributes = new
    {
        // See full payload with all supported attributes below
    }
};

var json = JsonSerializer.Serialize(payload);
using var content = new StringContent(json, Encoding.UTF8, "application/json");
var response = await client.PostAsync("https://app.quickfield.ai/public-api/v1/recovery_factor", content);
var body = await response.Content.ReadAsStringAsync();

Console.WriteLine((int)response.StatusCode);
Console.WriteLine(body);` : ''}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Request example (with Monte Carlo)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 2,
                      }}
                    >
{`curl -X POST "https://app.quickfield.ai/public-api/v1/recovery_factor" \\
  -H "Content-Type: application/json" \\
  -H "apikey: YOUR_API_KEY" \\
  -d '{
    "monte_carlo": {
      "attributes_deviations": 25,
      "n_simmulations": 100
    },
    "attributes": {
      "...": "see full payload below"
    }
  }'`}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Full request body (all supported attributes + units)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 2,
                      }}
                    >
{`{
  "attributes": {
    "absolute_goc_mark": 1597.750000, // m
    "absolute_gwc_mark": 1018.000000, // m
    "absolute_owc_mark": 2347.459333, // m
    "absolute_top_mark": 2364.861310, // m
    "average_effective_gas_saturation": 8.883333, // 1 (dimensionless)
    "average_effective_oil_saturation": 4.266663, // 1 (dimensionless)
    "average_effective_water_saturation": 4.807185, // 1 (dimensionless)
    "displacement_coefficient": 0.465210, // 1 (dimensionless)
    "gas_content": 79.859433, // m³/t
    "gas_density_at_reservoir_conditions": 1.085105, // kg/m³
    "gas_saturation_coefficient": 16.975750, // 1 (dimensionless)
    "gas_saturation_pressure_of_oil": 10.218470, // MPa
    "gas_viscosity_at_reservoir_conditions": 0.015967, // mPa·s
    "heterogeneity": 4.421092, // 1 (dimensionless)
    "hydrogen_sulfide_content": 0.000000, // %
    "initial_reservoir_pressure": 23.941532, // MPa
    "initial_reservoir_temperature": 83.459072, // °C
    "oil_and_gas_area": 83.346995745, // km2
    "oil_density_at_reservoir_conditions": 0.762267, // g/cm³
    "oil_density_at_surface_conditions": 0.841268, // g/cm³
    "oil_formation_volume_factor": 1.197667, // 1 (dimensionless)
    "oil_saturation_coefficient": 0.527645, // 1 (dimensionless)
    "oil_viscosity_at_reservoir_conditions": 1.219047, // mPa·s
    "paraffin_content_in_oil": 2.891087, // %
    "permeability": 62.961542, // mD
    "porosity_coefficient": 0.183614, // 1 (dimensionless)
    "productivity_coefficient": 8.133488, // m³/(day·MPa)
    "sandiness_coefficient": 0.420359, // 1 (dimensionless)
    "sulfur_content_in_oil": 0.760343, // %
    "water_density_at_surface_conditions": 1.014909, // g/cm³
    "water_viscosity_at_reservoir_conditions": 0.395459 // mPa·s
  }
}`}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Response example (without Monte Carlo)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 1,
                      }}
                    >
{`{
  "status": "ok",
  "code": 200,
  "recovery_factor": 0.3124
}`}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Response example (with Monte Carlo)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 1,
                      }}
                    >
{`{
  "status": "ok",
  "code": 200,
  "recovery_factor": 0.3124,
  "recovery_factor_p10": 0.2801,
  "recovery_factor_p50": 0.3102,
  "recovery_factor_p90": 0.3589
}`}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion disableGutters sx={{ mt: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      POST /public-api/v1/default_well_production
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Request example (without Monte Carlo)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 2,
                      }}
                    >
{`curl -X POST "https://app.quickfield.ai/public-api/v1/default_well_production" \\
  -H "Content-Type: application/json" \\
  -H "apikey: YOUR_API_KEY" \\
  -d '{
    "attributes": {
      "porosity_coefficient": 0.183614,
      "permeability": 62.961542,
      "productivity_coefficient": 8.133488,
      "oil_formation_volume_factor": 1.197667,
      "oil_viscosity_at_reservoir_conditions": 1.219047
    }
  }'`}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Request example (with Monte Carlo)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 2,
                      }}
                    >
{`curl -X POST "https://app.quickfield.ai/public-api/v1/default_well_production" \\
  -H "Content-Type: application/json" \\
  -H "apikey: YOUR_API_KEY" \\
  -d '{
    "monte_carlo": {
      "attributes_deviations": 20,
      "n_simmulations": 100
    },
    "attributes": {
      "porosity_coefficient": 0.183614,
      "permeability": 62.961542,
      "productivity_coefficient": 8.133488,
      "oil_formation_volume_factor": 1.197667,
      "oil_viscosity_at_reservoir_conditions": 1.219047
    }
  }'`}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Response example (without Monte Carlo)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 1,
                      }}
                    >
{`{
  "year_from_start": [0, 1, 2, "...", 50],
  "oil_1000t_year_ml": ["..."],
  "liquid_1000t_year_ml": ["..."],
  "water_cut_percent_year_ml": ["..."]
}`}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Response example (with Monte Carlo)
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: 'action.hover',
                        overflowX: 'auto',
                        fontFamily: 'Monospace',
                        fontSize: 13,
                        mb: 1,
                      }}
                    >
{`{
  "year_from_start": [0, 1, 2, "...", 50],
  "oil_1000t_year_ml": ["..."],
  "liquid_1000t_year_ml": ["..."],
  "water_cut_percent_year_ml": ["..."],
  "oil_1000t_year_p10": ["..."],
  "oil_1000t_year_p50": ["..."],
  "oil_1000t_year_p90": ["..."],
  "liquid_1000t_year_p10": ["..."],
  "liquid_1000t_year_p50": ["..."],
  "liquid_1000t_year_p90": ["..."],
  "water_cut_percent_year_p10": ["..."],
  "water_cut_percent_year_p50": ["..."],
  "water_cut_percent_year_p90": ["..."]
}`}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>

            {isEditing && (
              <Box mt={3} display="flex" gap={2}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving}
                  startIcon={isSaving ? <CircularProgress size={20} /> : null}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Account Information */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              Account Information
            </Typography>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Role
              </Typography>
              <Chip
                label={getRoleLabel(user.role)}
                color={getRoleColor(user.role)}
                size="small"
              />
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <Business sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Organization
              </Typography>
              <Typography variant="body1">
                {user.organization_name}
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Organization GUID
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  fullWidth
                  value={user.organization_id}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Tooltip title="Copy to clipboard">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(user.organization_id);
                            setSuccess('Organization GUID copied to clipboard');
                          }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Use this code when registering new users to your organization
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Box display="flex" alignItems="center">
                {user.is_active ? (
                  <Chip label="Active" color="success" size="small" />
                ) : (
                  <Chip label="Inactive" color="error" size="small" />
                )}
                {user.is_verified && (
                  <Verified sx={{ ml: 1, color: 'success.main', fontSize: 20 }} />
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box mb={1}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <CalendarToday sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Registration Date
              </Typography>
              <Typography variant="body2">
                {formatDate(user.created_at)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Last Updated
              </Typography>
              <Typography variant="body2">
                {formatDate(user.updated_at)}
              </Typography>
            </Box>
          </Paper>
        </Grid>

      </Grid>

      <Dialog
        open={Boolean(createdApiKeyValue)}
        onClose={() => setCreatedApiKeyValue(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>API key created</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Key added successfully. Save it now: this key will not be shown again.
          </Alert>

          <TextField
            fullWidth
            value={createdApiKeyValue || ''}
            label="API key"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (createdApiKeyValue) {
                        navigator.clipboard.writeText(createdApiKeyValue);
                        setSuccess('API key copied to clipboard');
                      }
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedApiKeyValue(null)} variant="contained">
            Save and close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(apiKeyToDelete)}
        onClose={() => {
          if (!deletingApiKey) {
            setApiKeyToDelete(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete API key?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to delete API key
            {apiKeyToDelete ? ` "${apiKeyToDelete.name}"` : ''}.
          </Typography>
          <Alert severity="warning">
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApiKeyToDelete(null)}
            disabled={deletingApiKey}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteApiKey}
            disabled={deletingApiKey}
          >
            {deletingApiKey ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
